import { NgModule } from '@angular/core';
import { Route, Routes, RouterModule } from '@angular/router';

import { HomeComponent } from './home.component';
import { FeaturesComponent } from './features.component';
import { PricingComponent } from './pricing.component';
import { GuideComponent } from './guide.component';
import { ContactComponent } from './contact/contact.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { TermsOfServiceComponent } from './terms-of-service/terms-of-service.component';
import { DashEnterpriseAuthComponent } from './dash-enterprise-auth/dash-enterprise-auth.component';
import { GuideEnterpriseComponent } from './enterprise/guide-enterprise/guide-enterprise.component';
import { GuideEnterpriseDashboardComponent } from './enterprise/guide-enterprise-dashboard/guide-enterprise-dashboard.component';
import { GuideEnterpriseMdmComponent } from './enterprise/guide-enterprise-mdm/guide-enterprise-mdm.component';
import { GuideEnterpriseDashboardConfigurationComponent } from './enterprise/guide-enterprise-dashboard-configuration/guide-enterprise-dashboard-configuration.component';
import { EnterpriseConfigurationValuesComponent } from './enterprise/enterprise-configuration-values/enterprise-configuration-values.component';
import { GuideEnterpriseAnalyticsComponent } from './enterprise/guide-enterprise-analytics/guide-enterprise-analytics.component';
import { DataDeletionComponent } from './data-deletion/data-deletion.component';
import { GuideEnterpriseUserAdministrationComponent } from './enterprise/guide-enterprise-user-administration/guide-enterprise-user-administration.component';
import { HardwareComponent } from './hardware/hardware.component';
import { GuideEnterpriseWebComponent } from './enterprise/guide-enterprise-web/guide-enterprise-web.component';
import { AboutEnterpriseComponent } from './about-enterprise/about-enterprise.component';

import { GUIDE_PAGES } from './guide-pages';
import { BLOG_ROUTES } from './blog/blog-routes';



const routes: Routes = [
  {
      path: 'privacy-policy',
      component: PrivacyPolicyComponent,
      data: { title: 'Privacy Policy - ' }
  },
  {
      path: 'terms-of-service',
      component: TermsOfServiceComponent,
      data: { title: 'Terms of Service - ' }
  },
  {
      path: 'hardware',
      component: HardwareComponent,
      data: {
        fullTitle: 'Meeting Room Display Hardware: Tablets, TVs & PoE Panels',
        description: 'Dash runs on iPads, Android tablets, phones and smart TVs you already own, or on dedicated room displays from leading manufacturers.'
      }
  },
  {
      path: 'about-enterprise',
      component: AboutEnterpriseComponent,
      data: {
        fullTitle: 'Dash Enterprise: Central Management for Meeting Room Displays',
        description: 'Deploy and manage Dash across every room from our web dashboard or your MDM. SSO, user roles, and room analytics.  For one room or hundreds.'
      }
  },
  {
      path: 'enterprise',
      component: GuideEnterpriseComponent,
      data: {
        fullTitle: 'Dash Enterprise documentation',
        description: 'Configure and manage your Dash displays centrally, using the web dashboard or your existing MDM.'
      }
  },
  {
      path: 'enterprise/dashboard',
      component: GuideEnterpriseDashboardComponent,
      data: {
        fullTitle: 'Dash Enterprise web management dashboard overview',
        description: 'Configure and manage your Dash displays centrally, using the Dash Enterprise web management dashboard.'
      }
  },
  {
      path: 'enterprise/dashboard-configuration',
      component: GuideEnterpriseDashboardConfigurationComponent,
      data: {
        fullTitle: 'Configure Dash devices using the web dashboard',
        description: 'Centrally manage the onboarding and configuration of your Dash displays'
      }
  },
  {
      path: 'enterprise/web-app',
      component: GuideEnterpriseWebComponent,
      data: {
        fullTitle: 'Dash Web App: Run a Room Display in Any Browser',
        description: 'Configure a device with a web browser as a Dash display, supporting all the features of the standard Dash app'
      }
  },
  {
      path: 'enterprise/configuration-options',
      component: EnterpriseConfigurationValuesComponent,
      data: {
        fullTitle: 'Dash Configuration Options Reference',
        description: 'Complete list of Dash configuration keys and the values each accepts, for use in the web dashboard or an MDM managed configuration.'
      }
  },
  {
      path: 'enterprise/analytics',
      component: GuideEnterpriseAnalyticsComponent,
      data: {
        fullTitle: 'Meeting Room Analytics in Dash Enterprise',
        description: 'Dash provides detailed room analytics to help you understand how your meeting spaces are being utilized.  Each chart provides unique insights.'
      }
  },
  {
      path: 'enterprise/user-administration',
      component: GuideEnterpriseUserAdministrationComponent,
      data: {
        fullTitle: 'Dash Enterprise User Management: Roles, 2FA & SSO' ,
        description: 'Add and remove team members, assign roles, and control who can manage devices and change display settings across your organisation\'s meeting rooms.  Connect Dash Enterprise to your IDP for SSO.'
      }
  },
  {
      path: 'enterprise/mdm',
      component: GuideEnterpriseMdmComponent,
      data: {
        fullTitle: 'Configure Dash Enterprise devices using MDM',
        description: 'Deploy and configure Dash through your MDM system using managed configurations.'
      }
  },
  // One explicit route per guide page, generated from the guide manifest so that each
  // gets its own title and description. Keep these flat (a single 'guide/<slug>' path
  // rather than a 'guide' parent with children): the guide templates are full of
  // relative links like '../quickStart' that resolve against the segment count.
  ...GUIDE_PAGES.map((page): Route => ({
    path: page.slug ? `guide/${page.slug}` : 'guide',
    component: GuideComponent,
    data: {
      fullTitle: page.title,
      description: page.description,
      article: page.article,
      subArticle: page.subArticle
    }
  })),
  // Unknown guide slugs land on the guide index rather than the home page.
  {
      path: 'guide/:id',
      redirectTo: '/guide'
  },
  // The blog index plus one route per published post, generated from the markdown in
  // src/content/blog at build time. Kept flat and unparameterized so the prerender
  // builder discovers and statically renders every post.
  ...BLOG_ROUTES,
  // Unknown post slugs land on the blog index rather than the home page.
  {
      path: 'blog/:slug',
      redirectTo: '/blog'
  },
  {
      path: 'contact',
      component: ContactComponent,
      data: {
        fullTitle: 'Contact Dash support',
        description: 'Get in touch with the Dash team for support, sales questions, or help setting up your meeting room displays.'
      }
  },
  {
    path: 'features',
    component: FeaturesComponent,
    data: {
      fullTitle: 'Conference Room Scheduler & Meeting Room Display',
      description: 'See room status at a glance, book in one tap, and reclaim rooms from no-shows. Free read-only display on any tablet, or $3.99 per display per month.'
    }
  },
  {
      path: 'pricing',
      component: PricingComponent,
      data: {
        fullTitle: 'Dash Pricing: Free Room Display, $3.99/Display/month for Booking',
        description: 'Dash pricing: free read-only room schedule display forever; room booking, check-in, branding, and more for US$3.99 per display per month; Enterprise with central management and analytics at custom pricing - contact us for details.'
      }
  },
  { path: '',
    component: HomeComponent,
    data: { title: '' }
  },
  {
      path: 'dash-enterprise-auth',
      component: DashEnterpriseAuthComponent,
      data: { fullTitle: 'Dash Enterprise Client Authorization' }
  },
  {
      path: 'data-deletion',
      component: DataDeletionComponent,
      data: { fullTitle: 'Request Deletion of Your Dash Data' }
  },
  { path: 'home',
      redirectTo: '/',
      pathMatch: 'full'
  },
  { path: '**',
      redirectTo: '/',
      pathMatch: 'full'
  },

];

@NgModule({
  imports: [RouterModule.forRoot(routes, { anchorScrolling: 'enabled',
    scrollOffset: () => typeof window !== 'undefined'
      ? [0, window.innerWidth < 800 ? 150 : window.innerHeight / 2 - 125]
      : [0, 0],
    scrollPositionRestoration: 'enabled' })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
